import { API } from '@/modules/api'
import type { TAssignedFeeData } from '@/modules/api/get/assignedFees'
import type { TUnassignedFeeData } from '@/modules/api/get/unassignedFees'
import type { TAssignedDepositData } from '@/modules/api/get/assignedDeposits'
import type { TMbaPowerschoolFeeCartLine, TStudentFeeSimpleCartLine } from '@/types/cart'
import { defineStore } from 'pinia'
import { parse, format } from 'date-fns'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'
import { shouldAutoAdd, hasUnsignedForms } from '@/mixins/OtherFeeHelper'
import { useCartStore } from '@/stores/cart'
import { useStore } from '@/stores'

export const createUniqueId = (feeId: number, orderItemTypeId: number) =>
  feeId + '::' + orderItemTypeId

export const useStudentFeeSimpleStore = defineStore('studentFeeSimple', {
  state: () => ({
    isLoaded: {} as Record<TStudentAccountId, boolean>,
    assignedFees: {} as Record<TStudentAccountId, TAssignedFeeData[]>,
    assignedDeposits: {} as Record<TStudentAccountId, TAssignedDepositData[]>,
    unassignedFees: {} as Record<TStudentAccountId, TUnassignedFeeData[]>
  }),
  getters: {
    isLoading(state) {
      return (studentAccountId: TStudentAccountId) => !(state.isLoaded[studentAccountId] ?? false)
    },
    getAssignedFees(state) {
      return (studentAccountId: TStudentAccountId) =>
        (state.assignedFees[studentAccountId] ?? []).map(fee => ({
          ...fee,
          date: parse(fee.date, 'yyyy-MM-dd', new Date())
        }))
    },
    getUnassignedFees(state) {
      return (studentAccountId: TStudentAccountId) => state.unassignedFees[studentAccountId] ?? []
    },
    getAssignedDeposits(state) {
      return (studentAccountId: TStudentAccountId) =>
        (state.assignedDeposits[studentAccountId] ?? []).map(deposit => ({
          ...deposit,
          date: parse(deposit.date, 'yyyy-MM-dd', new Date())
        }))
    }
  },

  actions: {
    async fetch(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      this.isLoaded[studentAccountId] = false
      const [assignedFees, unassignedFees, assignedDeposits] = await Promise.all([
        API.Get.assignedFees(studentAccountId),
        API.Get.unassignedFees(studentAccountId),
        API.Get.assignedDeposits(studentAccountId)
      ])
      // DTA-949 release gate (belt and braces — core stops listing them too):
      // with athletics off for the district, coupon book items are not offered
      // and a sold book waiting on someone else is not shown.
      const athleticsOn = useStore().athleticsOn(districtId)
      this.assignedFees[studentAccountId] = athleticsOn
        ? assignedFees
        : assignedFees.filter(fee => !fee.couponBookWaiting)
      this.unassignedFees[studentAccountId] = athleticsOn
        ? unassignedFees
        : unassignedFees.filter(fee => !fee.couponBookId)
      this.assignedDeposits[studentAccountId] = assignedDeposits
      this.autoAdd(districtId, studentAccountId)
      this.validateCart(districtId, studentAccountId)
      this.isLoaded[studentAccountId] = true
    },

    autoAdd(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      const store = useStore()
      // A district with no supported merchant account can't accept payments, so
      // there is no cart to auto-populate — adding fees would only produce a
      // phantom line and a failing order estimate (DTB-343).
      if (store.getDistrict(districtId)?.acceptingPayments === false) {
        return
      }
      this.assignedFees[studentAccountId].forEach(feeData => {
        if (shouldAutoAdd(feeData, studentAccountId)) {
          const cartData: Omit<TStudentFeeSimpleCartLine, 'orderItemType' | 'uniqueId'> = {
            studentAccountId: studentAccountId,
            feeId: feeData.feeId,
            orderItemTypeId: feeData.orderItemTypeId ?? OrderItemType.OtherFee,
            amount: feeData.amountDue,
            description: feeData.description,
            familyMaxId: feeData.familyMaxId,
            isPartial: feeData.allowPartial
          }

          if (feeData.familyMaxId && feeData.familyMaxId !== 0) {
            const student = store.getStudent(studentAccountId)
            cartData.familyAccountId = student.familyAccountId
          }

          this.addToCart(districtId, cartData)
        }
      })
      this.assignedDeposits[studentAccountId].forEach(depositData => {
        if (shouldAutoAdd(depositData, studentAccountId)) {
          const cartData: Omit<TStudentFeeSimpleCartLine, 'orderItemType' | 'uniqueId'> = {
            studentAccountId: studentAccountId,
            feeId: depositData.feeId,
            orderItemTypeId: depositData.orderItemTypeId ?? OrderItemType.AssignedDeposit,
            amount: depositData.amountDue,
            description: depositData.description,
            familyMaxId: depositData.familyMaxId,
            isPartial: depositData.allowPartial
          }
          if (depositData.familyMaxId && depositData.familyMaxId !== 0) {
            const student = store.getStudent(studentAccountId)
            cartData.familyAccountId = student.familyAccountId
          }

          this.addToCart(districtId, cartData)
        }
      })
    },

    /**
     * DTA-1036: put one assigned fee in the cart on purpose (the athletics
     * "Pay fee now"), whether or not autoAdd would have. Call after fetch().
     * Returns false when the student has no unpaid assigned fee with that id.
     */
    addAssignedFeeToCart(
      districtId: TDistrictId,
      studentAccountId: TStudentAccountId,
      feeId: number
    ): boolean {
      const feeData = (this.assignedFees[studentAccountId] ?? []).find(
        fee => Number(fee.feeId) === Number(feeId) && Number(fee.amountDue) > 0
      )
      if (!feeData) {
        return false
      }
      const cartData: Omit<TStudentFeeSimpleCartLine, 'orderItemType' | 'uniqueId'> = {
        studentAccountId: studentAccountId,
        feeId: feeData.feeId,
        orderItemTypeId: feeData.orderItemTypeId ?? OrderItemType.OtherFee,
        amount: feeData.amountDue,
        description: feeData.description,
        familyMaxId: feeData.familyMaxId,
        isPartial: feeData.allowPartial
      }
      if (feeData.familyMaxId && feeData.familyMaxId !== 0) {
        cartData.familyAccountId = useStore().getStudent(studentAccountId).familyAccountId
      }
      this.addToCart(districtId, cartData)
      return true
    },

    // This function is used to validate the cart lines for the student fee simple store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    validateCart(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      const cartStore = useCartStore()

      for (const line of cartStore.getCartLines(districtId) as TStudentFeeSimpleCartLine[]) {
        if (line.studentAccountId !== studentAccountId) continue

        // Handle OtherFee lines
        if (line.orderItemType === OrderItemType.OtherFee) {
          const fee = this.assignedFees[studentAccountId].find(fee => {
            return (
              createUniqueId(fee.feeId, fee.orderItemTypeId ?? OrderItemType.OtherFee) ===
              line.uniqueId
            )
          })
          if (!fee) {
            console.log('SFS fee not found, removing from cart', line.feeId)
            this.removeFromCart(districtId, line.feeId, OrderItemType.OtherFee)
            continue
          }
          // DTA-659: the cart is persisted (pinia `persist: true`) and rehydrated
          // verbatim, so the add-time form gate is never re-applied. A line added
          // before the item required a form — or in an earlier session, or via the
          // force path — otherwise sits in the cart invisibly and fails the whole
          // checkout server-side. Evict it here, at the boundary the cart is
          // actually reloaded through.
          if (hasUnsignedForms(fee)) {
            console.log('SFS fee now requires an unsubmitted form, removing from cart', line.feeId)
            this.removeFromCart(districtId, line.feeId, OrderItemType.OtherFee)
            continue
          }
          if (!fee.variablePricingEnabled && fee.amountDue < line.amount) {
            console.log('SFS fee amount is less than cart amount, adjusting cart', line.feeId)
            cartStore.updateCartLine(districtId, line.uniqueId, {
              ...line,
              amount: fee.amountDue
            })
          }
        }

        // Handle AssignedDeposit lines
        if (line.orderItemType === OrderItemType.AssignedDeposit) {
          const deposit = this.assignedDeposits[studentAccountId].find(dep => {
            return (
              createUniqueId(dep.feeId, dep.orderItemTypeId ?? OrderItemType.AssignedDeposit) ===
              line.uniqueId
            )
          })
          if (!deposit) {
            console.log('SFS assigned deposit not found, removing from cart', line.feeId)
            this.removeFromCart(districtId, line.feeId, OrderItemType.AssignedDeposit)
            continue
          }
          // Same guard for deposits — they run through the identical form gate.
          if (hasUnsignedForms(deposit)) {
            console.log(
              'SFS deposit now requires an unsubmitted form, removing from cart',
              line.feeId
            )
            this.removeFromCart(districtId, line.feeId, OrderItemType.AssignedDeposit)
            continue
          }
          if (deposit.amountDue < line.amount) {
            console.log('SFS deposit amount is less than cart amount, adjusting cart', line.feeId)
            cartStore.updateCartLine(districtId, line.uniqueId, {
              ...line,
              amount: deposit.amountDue
            })
          }
        }
      }
    },

    addToCart(
      districtId: TDistrictId,
      feeData: Omit<TStudentFeeSimpleCartLine, 'uniqueId' | 'orderItemType'>
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId, feeData.orderItemTypeId)
      const existingLine = cartStore.getCartLine(districtId, uniqueId)
      if (existingLine) {
        cartStore.updateCartLine(districtId, uniqueId, feeData)
      } else {
        cartStore.addCartLine(districtId, uniqueId, {
          ...feeData,
          orderItemType: feeData.orderItemTypeId,
          uniqueId
        })
      }
    },

    async assignFee(
      districtId: TDistrictId,
      studentAccountId: TStudentAccountId,
      unassignedFee: TUnassignedFeeData
    ) {
      API.Post.assignFee(districtId, studentAccountId, unassignedFee.menuItemId)
        .then(async () => {
          await this.fetch(districtId, studentAccountId)
        })
        .catch(error => {
          console.error('Failed to assign fee:', error)
        })
    },

    async removeFee(
      districtId: TDistrictId,
      studentAssignedFeeId: number,
      studentAccountId: TStudentAccountId
    ) {
      API.Post.removeOtherFee(districtId, studentAssignedFeeId)
        .then(() => {
          const assignedIndex = this.assignedFees[studentAccountId].findIndex(
            fee => fee.feeId === studentAssignedFeeId
          )
          this.assignedFees[studentAccountId].splice(assignedIndex, 1)
          this.removeFromCart(districtId, studentAssignedFeeId, OrderItemType.OtherFee)
        })
        .catch(error => {
          console.error('Failed to remove fee:', error)
        })
    },

    updateCartLine(
      districtId: TDistrictId,
      feeData: Partial<TStudentFeeSimpleCartLine> & { feeId: number }
    ) {
      const cartStore = useCartStore()
      if (feeData.orderItemTypeId === undefined) return
      const uniqueId = createUniqueId(feeData.feeId, feeData.orderItemTypeId)
      cartStore.updateCartLine(districtId, uniqueId, feeData)
    },

    removeFromCart(districtId: TDistrictId, feeId: number, orderItemTypeId: number) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeId, orderItemTypeId)
      cartStore.removeCartLine(districtId, uniqueId)
    }
  }
})
